import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ethers } from 'ethers';
import lbDialog from '../components/UI/Modals/lbDialog';
import { Copy, Gift, Link2, UserCheck, Users } from 'lucide-react';
import { useAppKitAccount } from '@reown/appkit/react';
import { useT } from '../i18n';
import {
  provider,
  lbreferral,
  bullvsbear,
  bestiariumAddress,
  luckBlocksAddress,
  ethLuckBlocksAddress,
  maticLuckBlocksAddress,
  CRANNILuckBlocksAddress,
} from '../.config';
import { bullsEyeAddress } from './BullsEye/config';
import ReferralABI from '../abi/LBReferral.json';
import { referralLink } from '../referral';

// every game that pays referrals: its commission and the token it pays in
const GAMES = [
  { key: 'bullVsBear', name: 'nav.bullVsBear', path: '/prediction', address: bullvsbear, rate: 'referral.rate.bullVsBear', token: 'USDT', decimals: 6 },
  { key: 'bullsEye', name: 'nav.bullsEye', path: '/bullseye', address: bullsEyeAddress, rate: 'referral.rate.bullsEye', token: 'USDT', decimals: 6 },
  { key: 'bestiarium', name: 'nav.bestiarium', path: '/bestiarium', address: bestiariumAddress, rate: 'referral.rate.bestiarium', token: 'CRANNI', decimals: 8 },
  { key: 'btc', name: 'tasks.name.btcDaily', path: '/', address: luckBlocksAddress, rate: 'referral.rate.lottery', token: 'USDT', decimals: 6 },
  { key: 'eth', name: 'tasks.name.ethDaily', path: '/ethereum', address: ethLuckBlocksAddress, rate: 'referral.rate.lottery', token: 'USDT', decimals: 6 },
  { key: 'pol', name: 'tasks.name.polDaily', path: '/matic', address: maticLuckBlocksAddress, rate: 'referral.rate.lottery', token: 'USDT', decimals: 6 },
  { key: 'cranni', name: 'tasks.name.cranniDaily', path: '/CRANNI', address: CRANNILuckBlocksAddress, rate: 'referral.rate.lottery', token: 'USDT', decimals: 6 },
].filter((g) => g.address);

const ZERO = ethers.constants.AddressZero;
const short = (a) => `${a.slice(0, 6)}…${a.slice(-4)}`;
const fmt = (v, digits = 4) => v.toLocaleString('en-US', { maximumFractionDigits: digits });

/** Platform-wide referrals: one link for every game, what it earned in each one */
const Referral = () => {
  const t = useT();
  const { address, isConnected } = useAppKitAccount();
  const [data, setData] = useState({ loading: true, count: 0, referrer: null, earned: {} });
  const link = referralLink(address);

  const load = useCallback(async () => {
    if (!address || !lbreferral) {
      setData({ loading: false, count: 0, referrer: null, earned: {} });
      return;
    }
    try {
      const registry = new ethers.Contract(lbreferral, ReferralABI.abi || ReferralABI, provider);
      const [count, referrer, ...amounts] = await Promise.all([
        registry.referralCount(address),
        registry.referrerOf(address),
        ...GAMES.map((g) => registry.earned(address, g.address).catch(() => ethers.constants.Zero)),
      ]);
      const earned = Object.fromEntries(GAMES.map((g, i) => [g.key, parseFloat(ethers.utils.formatUnits(amounts[i], g.decimals))]));
      setData({ loading: false, count: count.toNumber(), referrer: referrer === ZERO ? null : referrer, earned });
    } catch (err) {
      setData({ loading: false, count: 0, referrer: null, earned: {} });
    }
  }, [address]);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const totals = GAMES.reduce((acc, g) => ({ ...acc, [g.token]: (acc[g.token] || 0) + (data.earned[g.key] || 0) }), {});

  const copy = () => {
    navigator.clipboard?.writeText(link);
    lbDialog.fire({ icon: 'success', title: t('referral.copied'), timer: 1200, showConfirmButton: false });
  };

  return (
    <div className="lb-page w-full px-3 py-10 sm:px-6">
      <div className="mx-auto w-full max-w-5xl space-y-6">
        <header className="text-center">
          <p className="lb-eyebrow">{t('referral.eyebrow')}</p>
          <h1 className="lb-heading mt-3 text-3xl sm:text-4xl">
            <span className="lb-gradient-text">{t('referral.title')}</span>
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-white/65">{t('referral.blurb')}</p>
        </header>

        {!isConnected ? (
          <section className="lb-card flex flex-col items-center gap-3 p-8 text-center">
            <p className="text-sm text-white/60">{t('referral.connect')}</p>
            <appkit-button />
          </section>
        ) : (
          <>
            <section className="lb-card p-5 sm:p-6">
              <p className="lb-label mb-2 flex items-center gap-2">
                <Link2 className="h-4 w-4 text-brand-cyan" aria-hidden="true" /> {t('referral.yourLink')}
              </p>
              <div className="flex gap-2">
                <input readOnly value={link} onFocus={(e) => e.target.select()} className="lb-input notranslate flex-1 text-xs sm:text-sm" />
                <button type="button" onClick={copy} className="lb-btn lb-btn-primary shrink-0" aria-label={t('referral.copy')}>
                  <Copy className="mr-1 h-4 w-4" /> {t('referral.copy')}
                </button>
              </div>
              <p className="mt-2 text-xs text-white/45">{t('referral.linkNote')}</p>
            </section>

            <section className="grid gap-3 sm:grid-cols-4">
              <div className="lb-tile p-4">
                <p className="lb-label flex items-center gap-1.5">
                  <Users className="h-4 w-4" aria-hidden="true" /> {t('referral.referred')}
                </p>
                <p className="lb-num mt-1 font-display text-3xl text-white">{data.count}</p>
              </div>
              {['USDT', 'CRANNI'].map((token) => (
                <div key={token} className="lb-tile p-4">
                  <p className="lb-label">{t('referral.earnedIn', { token })}</p>
                  <p className="lb-num mt-1 font-display text-2xl text-emerald-300">{fmt(totals[token] || 0)}</p>
                </div>
              ))}
              <div className="lb-tile p-4">
                <p className="lb-label flex items-center gap-1.5">
                  <UserCheck className="h-4 w-4" aria-hidden="true" /> {t('referral.yourReferrer')}
                </p>
                <p className="notranslate lb-num mt-1 text-sm font-semibold text-white">{data.referrer ? short(data.referrer) : t('referral.none')}</p>
              </div>
            </section>
          </>
        )}

        <section className="lb-card overflow-hidden">
          <div className="border-b border-white/5 px-5 py-4">
            <h2 className="lb-heading flex items-center gap-2 text-lg">
              <Gift className="h-5 w-5 text-amber-300" aria-hidden="true" /> {t('referral.byGame')}
            </h2>
          </div>
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-white/40">
                <th className="px-5 py-2 font-normal">{t('referral.game')}</th>
                <th className="px-5 py-2 font-normal">{t('referral.commission')}</th>
                <th className="px-5 py-2 text-right font-normal">{t('referral.earned')}</th>
              </tr>
            </thead>
            <tbody>
              {GAMES.map((g) => (
                <tr key={g.key} className="border-t border-white/5">
                  <td className="px-5 py-3">
                    <Link to={g.path} className="font-semibold text-white hover:text-brand-cyan">
                      {t(g.name)}
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-white/65">{t(g.rate)}</td>
                  <td className="notranslate lb-num px-5 py-3 text-right text-white">
                    {isConnected ? `${fmt(data.earned[g.key] || 0)} ${g.token}` : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="lb-card space-y-2 p-5 text-sm text-white/65">
          <h2 className="lb-heading mb-1 text-lg text-white">{t('referral.howTitle')}</h2>
          <p>{t('referral.how1')}</p>
          <p>{t('referral.how2')}</p>
          <p>{t('referral.how3')}</p>
        </section>
      </div>
    </div>
  );
};

export default Referral;
